import axios from 'axios'
import moment from 'moment'
import { useContext, useEffect, useRef } from 'react'
import ReactPixel from 'react-facebook-pixel'
import ReactGA from 'react-ga4'
import { useLocation } from 'react-router'

import { AppContext } from 'providers/context/App'
import { SKOS_HORIZON_EVENTS } from 'utils/constants/EndpointUrls'
import { GA4_EVENTS } from 'utils/constants/Enums'
import {
  CONFIRM_APPOINTMENT_ROUTE,
  DEFAULT_CURRENCY,
  LASER_CONFIRM_APPOINTMENT_ROUTE,
  LOCATION_STATE_CONFIG,
} from 'utils/constants/Helpers'
import {
  AppointmentDateTimeSelectData,
  ContinueORSkipEnhancementData,
  LocationSelectedData,
  MembershipFlowStartedData,
  MembershipViewCartData,
  PurchaseData,
  RemoveFromCartData,
} from 'utils/types/ga4eventsTypes'
import {
  EventHorizonPayload,
  EventHorizonResponse,
} from 'utils/types/membershipTypes'
import { AppData, FacebookEvents, OrderUPF } from '../utils/types/appTypes'

/**
 *
 * GOOGLE AND FACEBOOK ANALYTICS
 *
 */

/**
 *
 * @param page path name
 *
 * This function will add path for tracking
 */
export const TrackPage = (page: string, userId: string) => {
  ReactGA.set({ user_id: userId })
  ReactGA.send({ hitType: 'pageview', page })
}

export const useTrackPage = () => {
  const { appMasterData } = useContext(AppContext)
  const location = useLocation()
  const prevPathname = useRef<string | null>(null)

  useEffect(() => {
    const userBlvdId = appMasterData.userInfo.boulevard_id
    if (userBlvdId && prevPathname.current !== location.pathname) {
      TrackPage(location.pathname, userBlvdId)
      prevPathname.current = location.pathname // Update the stored pathname
    }
  }, [location.pathname, appMasterData])
}

/**
 *
 * @param title Event Title
 * @param data Data
 *
 */
const FbPixelEvent = (title: string, data: Partial<FacebookEvents>) => {
  ReactPixel.track(title, data)
}

/**
 * ViewContent is to be fired when the widget is launched
 */
export const ViewContentAnalytics = () => {
  const data = {
    content_name: 'Facial',
    content_type: 'BookAFacial',
    content_category: 'Facial Service',
  }
  FbPixelEvent('ViewContent', data)
}

/**
 *
 * AddToCart is to be launched when the Continue button is pushed once a location is selected
 */
export const AddToCartAnalytics = () => {
  const data = {
    content_type: 'BookAFacial',
    content_category: 'Facial Service',
  }
  FbPixelEvent('AddToCart', data)
}

/**
 *
 * FindATimeDateChange is to be launched when a time is selected
 */
export const FindATimeDateChangeAnalytics = (date: string) => {
  const data = {
    date,
    content_category: 'Facial Service',
  }
  FbPixelEvent('FindATimeDateChange', data)
}

/**
 *
 * @param userInfo
 *
 * InitiateCheckout is to be fired when the confirm appointment screen is loaded
 */
export const InitiateCheckoutAnalytics = (appMasterData: AppData) => {
  const data = {
    value: appMasterData.cart.summary.subtotal / 100,
    num_items: 1,
    currency: 'USD',
    content_name: 'Facial',
  }
  FbPixelEvent('InitiateCheckout', data)
}

/**
 *
 * Purchase is to be fired when the appointment is confirmed through the Blvd API
 */
export const PurchaseAnalytics = (appMasterData: AppData) => {
  const data = {
    value: appMasterData.cart.summary.subtotal / 100,
    num_items: 1,
    currency: 'USD',
    content_type: 'BookAFacial',
    contents: [
      {
        customer_status:
          appMasterData?.previousBookedAppointments?.length > 0
            ? 'returning_customer'
            : 'new_customer',
      },
    ],
  }
  FbPixelEvent('Purchase', data)
}

export const triggerConfirmationPageLoadEvent = (
  eventName: string,
  appMasterData: AppData,
  bookingType: string
) => {
  const data = {
    type: bookingType,
    currency: 'USD',
    transaction_id: appMasterData?.cart?.appointmentId ?? '',
    value: appMasterData?.cart?.summary?.subtotal / 100,
    items: appMasterData?.selectedEnhancements?.map((item) => item.name) ?? '',
    item_name:
      bookingType === 'Membership'
        ? 'Membership'
        : (appMasterData.selectedFacial ?? ''),
    location_name: appMasterData.location.name ?? '',
    location_state:
      LOCATION_STATE_CONFIG[appMasterData?.location?.address?.state] ?? '',
    coupon: appMasterData?.cart?.promoCodes,
    timeslot_selected:
      moment
        .tz(
          bookingType === 'Membership'
            ? new Date()
            : appMasterData?.selectedTime?.startTime,
          appMasterData?.location?.tz
        )
        .format('H:mm') ?? '',
    date_selected:
      moment(appMasterData.selectedDate).format('DD-MM-YYYY') ?? '',
    userId: appMasterData?.userInfo?.boulevard_id ?? '',
  }
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', eventName, data)
}

export const triggerPurchaseConversionEvent = (transId?: string) => {
  window.gtag('event', 'conversion', {
    send_to: 'AW-933034885/utcxCKWmyswZEIX387wD',
    transaction_id: transId ?? '',
  })
}

export const triggerFranchiseeConversionEvent = (transId?: string) => {
  window.gtag2('event', 'conversion', {
    send_to: 'AW-10851896489/ZBfhCJ2tyswZEKmZy7Yo',
    transaction_id: transId ?? '',
  })
}

export const triggerHealthCheckYesOrNoEvent = (
  location_name: string,
  eventName: string,
  userId: string,
  buttonType: boolean
) => {
  let dateData = buttonType
    ? { date: new Date().toString() }
    : { event_timestamp: new Date().toString() }

  const ga4Data = {
    location_name,
    userId,
    ...dateData,
  }

  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', eventName, ga4Data)
}

export const triggerConfirmationPageEvent = (
  eventName: string,
  userId: string
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', eventName, {
      userId,
    })
}

export const triggerSignInBookingFlowEvent = (
  page_location: string,
  page_referrer: string
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', 'sign_in_booking_flow_ga4', {
      page_location,
      page_referrer,
    })
}

export const agreeMembershipEventHorizon = async (
  email: string,
  phone: string,
  customer_id: string,
  boulevard_customer_id: string,
  date: string | Date,
  hostname: string
) => {
  const payload: EventHorizonPayload = {
    data: {
      email,
      phone,
      customer_id,
      boulevard_customer_id,
      date,
      hostname,
    },
    eventType: 'membership_agreement',
  }

  const headers = {
    Authorization: `Bearer ${process.env.REACT_APP_EVENT_HORIZON_TOKEN}`,
    'Content-Type': 'application/json',
  }

  try {
    const response = await axios.post<EventHorizonResponse>(
      SKOS_HORIZON_EVENTS,
      payload,
      {
        headers,
      }
    )
    return response.data
  } catch (error: any) {
    throw new Error(error.message)
  }
}

export const triggerContinueEnhancementSelectEvent = (
  data: ContinueORSkipEnhancementData,
  eventName: string
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', eventName, data)
}

export const triggerSkipEnhancementButtonEvent = (
  data: ContinueORSkipEnhancementData,
  eventName: string
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', eventName, data)
}

export const triggerViewCartEvent = (appMasterData: AppData) => {
  const data = {
    type: 'Booking',
    currency: 'USD',
    value: appMasterData?.cart?.summary?.subtotal / 100,
    items: appMasterData?.selectedEnhancements?.map((item) => item.name) ?? '',
    item_name: appMasterData.selectedFacial ?? '',
    location_name: appMasterData.location.name ?? '',
    location_state:
      LOCATION_STATE_CONFIG[appMasterData?.location?.address?.state] ?? '',
    userId: appMasterData?.userInfo?.boulevard_id ?? '',
    page_location: `${window.location.origin}${appMasterData.isLaserFacial ? LASER_CONFIRM_APPOINTMENT_ROUTE : CONFIRM_APPOINTMENT_ROUTE}`,
    page_referrer: window.location.href,
  }
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', GA4_EVENTS.VIEW_CART_GA4, data)
}

export const triggerRemoveFromCartEvent = (data: RemoveFromCartData) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', GA4_EVENTS.REMOVE_FROM_CART_GA4, data)
}

export const triggerAppointmentDateTimeSelectEvent = (
  data: AppointmentDateTimeSelectData
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', GA4_EVENTS.APPOINTMENT_DATE_TIME_SELECTED, data)
}

export const triggerMembershipFlowStartedEvent = (
  data: MembershipFlowStartedData
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', GA4_EVENTS.MEMBERSHIP_FLOW_STARTED_GA4, data)
}

export const triggerLocationSelectedEvent = (
  data: LocationSelectedData,
  eventName: string
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', eventName, data)
}

export const triggerMembershipViewCartEvent = (
  data: MembershipViewCartData
) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', GA4_EVENTS.MEMBERSHIP_VIEW_CART_GA4, data)
}

export const triggerPurchaseEvent = (data: PurchaseData) => {
  process.env.REACT_APP_GA_EVENTS_ENABLE &&
    window.gtag2('event', GA4_EVENTS.PURCHASE_GA4, data)
}

export const upfPushOrder = ({
  order_id,
  order_name,
  amount = 0,
  items,
  customer,
}: OrderUPF) => {
  if (!window?._upf?.push) return
  window._upf.push([
    'order',
    {
      order_id,
      order_name,
      amount: amount / 100,
      currency: DEFAULT_CURRENCY,
      items,
      customer,
    },
  ])
}
